import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Lock } from 'lucide-react';
import { login, isAuthenticated } from '../../lib/adminApi';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { useToast } from '../../hooks/use-toast';
import { useSEO } from '../../hooks/useSEO';

const AdminLogin = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);

  useSEO({ title: 'Admin Login | Hei Web Creations' });

  useEffect(() => {
    if (isAuthenticated()) {
      navigate('/admin', { replace: true });
    }
  }, [navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await login(email, password);
      toast({ title: 'Welcome back', description: 'Logged in successfully.' });
      navigate('/admin', { replace: true });
    } catch (err) {
      toast({
        title: 'Login failed',
        description: err.response?.data?.detail || 'Invalid credentials.',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#1A1A1A] px-6" data-testid="admin-login-page">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="w-full max-w-md"
      >
        <div className="bg-white rounded-3xl p-10 shadow-2xl">
          <div className="w-14 h-14 rounded-2xl bg-[#C8102E] flex items-center justify-center mb-8">
            <Lock className="text-white" size={24} />
          </div>
          <h1 className="text-3xl font-bold text-[#1A1A1A] mb-2 tracking-tight">Admin Access</h1>
          <p className="font-mono text-sm text-gray-400 mb-8">~/blog-cms — sign in to continue</p>

          <form onSubmit={handleSubmit} className="space-y-5">
            <div>
              <Label htmlFor="email" className="text-[#1A1A1A] font-semibold mb-2 block">Email</Label>
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="border-gray-300 focus:border-[#C8102E] focus:ring-[#C8102E]"
                data-testid="admin-email-input"
              />
            </div>
            <div>
              <Label htmlFor="password" className="text-[#1A1A1A] font-semibold mb-2 block">Password</Label>
              <Input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                className="border-gray-300 focus:border-[#C8102E] focus:ring-[#C8102E]"
                data-testid="admin-password-input"
              />
            </div>
            <Button
              type="submit"
              disabled={loading}
              className="w-full bg-[#1A1A1A] hover:bg-[#C8102E] text-white py-6 rounded-full transition-all duration-300 disabled:opacity-50"
              data-testid="admin-login-button"
            >
              {loading ? 'Signing in…' : 'Sign In'}
            </Button>
          </form>
        </div>
      </motion.div>
    </div>
  );
};

export default AdminLogin;
